import { useEffect, useState } from 'react'
import { Mail, Send } from 'lucide-react'
import { api } from '@/util/api'
import { AdminLayout } from '@/components/admin/AdminLayout'
import {
  Badge,
  Button,
  Notice,
  Select,
  Skeleton,
  SkeletonGroup,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHeader,
  TableHeaderCell,
  TableRow,
  useToast,
  type BadgeTone,
} from '@/components/play'
import type { WaitlistEntryResource } from '@/types/admin'

const STATUS_LABELS: Record<string, string> = { pending: 'Pending', invited: 'Invited', converted: 'Converted' }

const statusFilterItems = [
  { value: 'all', label: 'All statuses' },
  ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
]

function statusBadgeTone(status: string): BadgeTone {
  switch (status) {
    case 'converted': return 'success'
    case 'invited': return 'neutral'
    default: return 'quiet'
  }
}

export function AdminPrivateBetaPage() {
  const [entries, setEntries] = useState<WaitlistEntryResource[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [invitingId, setInvitingId] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    loadEntries()
  }, [statusFilter])

  async function loadEntries() {
    setLoading(true)
    setFailed(false)
    try {
      const params: Record<string, string> = {}
      if (statusFilter !== 'all') params.status = statusFilter
      const res = await api.getAdminPrivateBetaWaitlistEntries(params)
      setEntries(res.data)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  async function handleSendInvite(waitlistEntryId: string) {
    setInvitingId(waitlistEntryId)
    try {
      await api.sendAdminPrivateBetaInvite(waitlistEntryId)
      toast({
        title: 'Invite sent',
        description: 'The private beta invite email was sent.',
        tone: 'success',
      })
      loadEntries()
    } catch (err: any) {
      const apiMessage = err?.response?.data?.error?.message
      toast({
        title: 'Invite failed',
        description: apiMessage || 'Could not send invite email.',
        tone: 'error',
      })
    } finally {
      setInvitingId(null)
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-3xl font-semibold">Private beta</h1>
          <Select
            className="w-[170px]"
            label="Status"
            items={statusFilterItems}
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v ?? 'all')}
          />
        </div>

        {loading ? (
          <SkeletonGroup label="Loading private beta requests" className="grid gap-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
          </SkeletonGroup>
        ) : failed ? (
          <Notice tone="error" action={<Button variant="secondary" size="compact" onClick={() => void loadEntries()}>Try again</Button>}>
            We couldn’t load the waitlist.
          </Notice>
        ) : (
          <Table caption="Private beta waitlist" hideCaption>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Main use case</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Requested</TableHeaderCell>
                <TableHeaderCell>Invited</TableHeaderCell>
                <TableHeaderCell type="actions"><span className="sr-only">Actions</span></TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableHeaderCell scope="row">{entry.attributes.email}</TableHeaderCell>
                  <TableCell className="max-w-[320px] text-neutral-7">
                    {entry.attributes.main_use_case}
                  </TableCell>
                  <TableCell>
                    <Badge tone={statusBadgeTone(entry.attributes.status)}>
                      {STATUS_LABELS[entry.attributes.status] ?? entry.attributes.status}
                    </Badge>
                  </TableCell>
                  <TableCell type="data" className="text-neutral-7">
                    {new Date(entry.attributes.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell type="data" className="text-neutral-7">
                    {entry.attributes.invited_at
                      ? new Date(entry.attributes.invited_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell type="actions">
                    <Button
                      size="compact"
                      variant="secondary"
                      loading={invitingId === entry.id}
                      disabled={entry.attributes.status === 'converted'}
                      onClick={() => handleSendInvite(entry.id)}
                      aria-label={`Invite ${entry.attributes.email}`}
                    >
                      {invitingId !== entry.id && <Send />}
                      Invite
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableEmpty colSpan={6} icon={<Mail />}>
                  {statusFilter === 'all' ? 'No requests yet' : 'No requests with this status'}
                </TableEmpty>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </AdminLayout>
  )
}
